import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import React from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import '@Hub/root.ngz.scss';
import '@Hub/root.scss';
import AppTheme from '@Hub/Theme';

import AppContext from '@core/context';
import usePageViewMetric from '@core/hooks/usePageViewMetric';
import { InitializeProjectStore, useSuperHubStore } from '@core/store';
import { useScrollTarget } from '@core/utils/ScrollTarget';
import ScrollTop from '@core/utils/ScrollTop';

import HARContext from '@routes/Reference/context/HARContext';
import SuggestionsRouter from '@routes/Suggestions';
import SuperHubRoutes from '@routes/SuperHub/Routes';
import { SuperHubRoutePaths } from '@routes/SuperHub/types';

import DeprecationWarning from '@ui/DeprecationWarning';
import { NotificationToaster } from '@ui/Notification';

import ContentWithOwlbot from './components/ContentWithOwlbot';
import { CurrentPageProvider } from './context/CurrentPageContext';
import { InitializeSwr } from './swr';

/**
 * App Components
 */
const AppNav = loadable(() => import('./Header'), { ssr: true });
const AppFooter = loadable(() => import('./Footer'), { ssr: true });

/**
 * Route Components
 */
const Changelog = loadable(() => import('@routes/Changelog'), { ssr: true });
const CustomPage = loadable(() => import('@routes/CustomPage'), { ssr: true });
const DiscussionForum = loadable(() => import('@routes/Discuss'), { ssr: true });
const DocsPage = loadable(() => import('@routes/Doc'), { ssr: true });
const OAuthRedirect = loadable(() => import('@routes/Reference/OAuthRedirect'), { ssr: true });
const Recipes = loadable(() => import('@routes/Tutorials'), { ssr: true });
const ReferencePage = loadable(() => import('@routes/Reference'), { ssr: true });
const GraphQLPage = loadable(() => import('@routes/GraphQL'), { ssr: false });
const PageNotFound = loadable(() => import('@routes/PageNotFound'), { ssr: true });
const LandingPage = loadable(() => import('@routes/Landing'), { ssr: true });
const SuperHub = loadable(() => import('@routes/SuperHub'), { ssr: true });

function AppRoutes(props) {
  const [isSuperHub] = useSuperHubStore(s => [s.isSuperHub]);
  const { pathname } = useLocation();
  const { ref } = useScrollTarget();

  // Capture Page View Doc Metric on page navigation/load
  usePageViewMetric();

  return (
    <Switch>
      {
        // The 404 route has to be at the top of this switch
        // to account for optionally :slugged? route paths.
        !!props.is404 && (
          <Route path="*">
            <PageNotFound {...props} />
          </Route>
        )
      }
      {!!isSuperHub && (
        <Switch>
          <Route exact path="/">
            <SuperHubRoutes {...props} />
          </Route>
          <Route path={Object.values(SuperHubRoutePaths)}>
            <SuperHubRoutes {...props} />
          </Route>
        </Switch>
      )}
      <Route path="/docs/:slug?">
        <ScrollTop deps={[pathname]} smooth target={ref} />
        <DocsPage {...props} />
      </Route>
      <Route path="/reference/:slug?">
        <ScrollTop deps={[pathname]} smooth target={ref} />
        <HARContext>
          <ReferencePage {...props} />
        </HARContext>
      </Route>
      <Route path="/oauth2-redirect">
        <ScrollTop deps={[pathname]} smooth target={ref} />
        <OAuthRedirect {...props} />
      </Route>
      <Route path="/graphql">
        <ScrollTop deps={[pathname]} smooth target={ref} />
        <GraphQLPage {...props} />
      </Route>
      <Route path="/changelog/:slug?">
        <Changelog {...props} />
      </Route>
      <Route path="/page/:slug?">
        <CustomPage {...props} />
      </Route>
      <Route path="/recipes">
        <Recipes {...props} />
      </Route>
      <Route path="/discuss*/:slug?">
        <DiscussionForum {...props} />
      </Route>
      {/* TODO: this route makes the suggested edits preview button work from angular.
              remove it once we're rendering suggested edits in React! */}
      <Route path="/suggested-edits/:slug/preview">
        <DocsPage {...props} suggestedEditsPreview />
      </Route>
      <Route path="/(edit|suggested-edits)(/*)?">
        <SuggestionsRouter {...props} />
      </Route>
      <Route exact path="/">
        <LandingPage {...props} />
      </Route>
      <Route>
        <PageNotFound {...props} />
      </Route>
    </Switch>
  );
}

AppRoutes.propTypes = {
  is404: PropTypes.bool,
};

const App = ({
  children,
  config,
  context,
  glpString: enterpriseLP,
  isDetachedProductionSite,
  isFramePreview,
  ...props
}) => {
  return (
    <InitializeSwr>
      <InitializeProjectStore data={context.projectStore.data} legacyData={context.project}>
        <AppContext
          apiBaseUrl={props.apiBaseUrl}
          baseUrl={props.baseUrl}
          config={config}
          context={context}
          enterpriseLP={enterpriseLP}
          isDetachedProductionSite={isDetachedProductionSite}
          language={props.lang}
          meta={props.meta}
          name="Hub"
          rdmd={props.rdmd}
        >
          <AppTheme>
            <SuperHub isFramePreview={isFramePreview} {...props}>
              <CurrentPageProvider>
                <ContentWithOwlbot>
                  {children}

                  <AppNav {...props} enterpriseLP={enterpriseLP} isDetachedProductionSite={isDetachedProductionSite} />

                  {!!props.version?.is_deprecated && (
                    <DeprecationWarning
                      currentVersion={props.version.version}
                      lang={props.lang}
                      project={context.project}
                    />
                  )}

                  <AppRoutes {...props} />

                  <AppFooter isDetachedProductionSite={isDetachedProductionSite} />
                  <div className="ModalWrapper" id="ChatGPT-modal" />
                  {/* Top-level Toaster for displaying notifications in Hub */}
                  <NotificationToaster />
                </ContentWithOwlbot>
              </CurrentPageProvider>
            </SuperHub>
          </AppTheme>
        </AppContext>
      </InitializeProjectStore>
    </InitializeSwr>
  );
};

/**
 * @todo When/if we ever convert this file to TS, reference `HubResponseProps`
 * types from packages/iso/src/types/hub.ts.
 */
App.propTypes = {
  apiBaseUrl: PropTypes.string,
  baseUrl: PropTypes.string,
  branch: PropTypes.object,
  children: PropTypes.node,
  config: PropTypes.shape({
    asset_url: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    domain: PropTypes.string,
    domainFull: PropTypes.string,
    legacy_asset_url: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    metrics: PropTypes.shape({
      dashUrl: PropTypes.string,
      defaultUrl: PropTypes.string,
    }),
    releaseVersion: PropTypes.string,
    sentry: PropTypes.shape({
      dsn: PropTypes.string,
      enabled: PropTypes.bool,
    }),
    sslGenerationService: PropTypes.string,
  }),
  context: PropTypes.shape({
    project: PropTypes.object,
    projectStore: PropTypes.object,
    terms: PropTypes.arrayOf(PropTypes.object),
    user: PropTypes.object,
    variables: PropTypes.object,
    version: PropTypes.object,
  }),
  doc: PropTypes.object,
  gitVersion: PropTypes.object,
  glpString: PropTypes.string,
  is404: PropTypes.bool,
  isDetachedProductionSite: PropTypes.bool,
  isEnterpriseParent: PropTypes.bool,
  isFramePreview: PropTypes.bool,
  lang: PropTypes.string,
  meta: PropTypes.object,
  oasDefinition: PropTypes.object,
  rdmd: PropTypes.object,
  sidebar: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  version: PropTypes.object,
  versions: PropTypes.object,
};

export default App;
